import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Save, Plus, Trash2, Users, Search, UserCheck, Info, ChevronDown, ChevronUp, Cloud, CloudOff, RefreshCw, AlertTriangle, ClipboardPaste, Check, X } from 'lucide-react';
import {
  getSegment, createSegment, updateSegment, edgeFunctions,
  listAllContactsForPicker, listSegmentMembers, setSegmentMembers,
  isSegmentUsable, STUDIO_OPTIONS,
  type Segment, type Contact,
} from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import type { FilterCondition, FilterGroup } from '../../types';
import { PageHeader } from '../../components/PageHeader';
import { HouseholdBadge } from '../../components/HouseholdBadge/HouseholdBadge';
import { formatNumber, formatDateTime } from '../../lib/format';

const ORGANIZATIONS = [
  'Spandau', 'Spandau gGmbH', 'Charlottenburg', 'Steglitz', 'Tegel',
  'Waltersdorf', 'Mariendorf', 'Lübars', 'e.V.',
];
const SCHOOLS = ORGANIZATIONS;
const MEMBER_STATUS = ['aktiv', 'inaktiv', 'probe', 'gekündigt'];

const FIELD_LABELS: Partial<Record<FilterCondition['field'], string>> = {
  household_studio:        'Studio (Familie) ⭐',
  household_is_member:     'Mitglied im Haushalt ⭐',
  household_has_trainer:   'Trainer im Haushalt ⭐',
  household_member_name:   'Name im Haushalt enthält ⭐',
  household_youngest_member_birth_date: 'Familie mit Mitglied im Alter ⭐',
  organization:  'Studio (Primärkontakt)',
  birth_date:    'Alter (Jahre)',
  school:        'Schule (legacy)',
  member_status: 'Mitglieder-Status (alt)',
  opt_in:        'Marketing-Opt-In',
  tags:          'Schlagwort (Tag)',
  bounce_status: 'E-Mail-Bounce',
};
const OP_LABELS_DEFAULT: Partial<Record<FilterCondition['op'], string>> = {
  eq: 'ist', neq: 'ist nicht', in: 'ist eines von',
  contains: 'enthält', is_null: 'ist nicht gesetzt', is_not_null: 'ist gesetzt',
};
const OP_LABELS_BOOLEAN: Partial<Record<FilterCondition['op'], string>> = {
  eq: 'ist', neq: 'ist nicht',
};
// Alters-Felder kennen nur einen Operator: einen Min–Max-Bereich in Jahren.
// Das Backend (resolve-segment) rechnet age_between intern in einen
// Geburtsdatums-Bereich auf der passenden Spalte um.
const OP_LABELS_AGE: Partial<Record<FilterCondition['op'], string>> = {
  age_between: 'ist zwischen',
};
const DEFAULT_AGE_RANGE: [number, number] = [0, 17];
const HOUSEHOLD_BOOLEAN_FIELDS = new Set<FilterCondition['field']>([
  'household_is_member', 'household_has_trainer',
]);
// Felder, die per age_between gefiltert werden (Min–Max-Jahre-Eingabe).
const AGE_FIELDS = new Set<FilterCondition['field']>([
  'birth_date', 'household_youngest_member_birth_date',
]);

/** Tabellen-Renderer fuer eine Empfaenger-/Mitglieder-Liste (dynamisch oder statisch). */
function RecipientTable({ contacts, searchValue, onSearchChange, emptyMessage }:
  { contacts: Contact[]; searchValue: string; onSearchChange: (v: string) => void; emptyMessage: string }) {
  return (
    <>
      <div className="search-input-wrapper" style={{ maxWidth: 360, marginBottom: '0.75rem' }}>
        <Search size={16} />
        <input className="search-input" placeholder="In Mitgliedern suchen..."
          value={searchValue} onChange={(e) => onSearchChange(e.target.value)} />
      </div>
      <div style={{ maxHeight: 560, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
        <table className="data-table" style={{ border: 'none', borderRadius: 0 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr><th>E-Mail</th><th>Name</th><th>Studio</th><th>Status</th><th>Opt-In</th></tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{c.email}</td>
                <td>{[c.first_name, c.last_name].filter(Boolean).join(' ') || <em style={{ color: 'var(--text-tertiary)' }}>—</em>}</td>
                <td>{c.organization ?? c.school ?? <em style={{ color: 'var(--text-tertiary)' }}>—</em>}</td>
                <td>
                  {c.is_member ? (
                    <span className="status-badge" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}>Mitglied</span>
                  ) : c.crm_id ? (
                    <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>{c.customer_status ?? 'Lead'}</span>
                  ) : (
                    <span className="status-badge" style={{ background: 'var(--info-light)', color: 'var(--info-dark)' }}>manuell</span>
                  )}
                </td>
                <td>
                  {c.unsubscribed_at ? (
                    <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)' }}>abgemeldet</span>
                  ) : c.is_active === false ? (
                    <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>inaktiv</span>
                  ) : c.opt_in ? (
                    <span className="status-badge" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}>versendbar</span>
                  ) : (
                    <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>kein Opt-In</span>
                  )}
                </td>
              </tr>
            ))}
            {contacts.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-tertiary)' }}>{emptyMessage}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function SegmentEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === 'new';

  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [form, setForm] = useState<Partial<Segment>>({
    name: '', description: '', is_dynamic: true,
    studio_scope: null, admin_only: false, always_included: false, valid_until: null,
  });
  const [filter, setFilter] = useState<FilterGroup>({
    operator: 'AND',
    conditions: [{ field: 'opt_in', op: 'eq', value: true }],
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState('');
  const [showRecipients, setShowRecipients] = useState(true);
  const [recipientSearch, setRecipientSearch] = useState('');

  // Bulk-Import: User kopiert eine Liste von E-Mail-Adressen rein, das System
  // matcht sie gegen vorhandene Kontakte und uebernimmt die Treffer in die Auswahl.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState<{ matched: number; alreadyIn: number; notFound: string[] } | null>(null);

  const segmentQuery = useQuery({ queryKey: ['segment', id], queryFn: () => getSegment(id!), enabled: !isNew });
  const membersQuery = useQuery({ queryKey: ['segment-members', id], queryFn: () => listSegmentMembers(id!), enabled: !isNew });

  // Bei STATISCHEN Gruppen brauchen wir die Kontakt-Lookup-Tabelle, um die
  // ausgewaehlten Mitglieder oben als Tabelle UND unten im Picker rendern zu koennen.
  const allContactsQuery = useQuery({
    queryKey: ['all-contacts-picker'],
    queryFn: listAllContactsForPicker,
    enabled: form.is_dynamic === false,
  });

  const resolveMutation = useMutation({
    mutationFn: () => edgeFunctions.resolveSegment(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });

  useEffect(() => {
    if (segmentQuery.data) {
      setForm(segmentQuery.data);
      if (segmentQuery.data.filter_query) {
        try { setFilter(segmentQuery.data.filter_query as unknown as FilterGroup); } catch { /* ignore */ }
      }
    }
  }, [segmentQuery.data]);
  useEffect(() => { if (membersQuery.data) setSelectedIds(new Set(membersQuery.data)); }, [membersQuery.data]);
  useEffect(() => {
    // Direkt aus den Query-Daten lesen: `form` ist beim ersten Lauf dieses
    // Effekts noch der leere Initial-State (Hydration passiert im Effekt
    // darueber, greift aber erst im naechsten Render) — die Mitglieder-Liste
    // dynamischer Gruppen wurde dadurch nie automatisch geladen.
    const seg = segmentQuery.data;
    if (!isNew && seg?.is_dynamic && seg.filter_query) resolveMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, segmentQuery.data?.id]);

  // Brevo-Sync: Toggle (persistiert sofort) und manueller Sync.
  const brevoToggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (isNew || !id) throw new Error('Segment muss zuerst gespeichert werden.');
      return updateSegment(id, { brevo_sync_enabled: enabled });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['segment', id] }),
  });
  const brevoSyncMutation = useMutation({
    mutationFn: () => edgeFunctions.syncSegmentToBrevo(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['segment', id] }),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Partial<Segment> = {
        ...form,
        filter_query: form.is_dynamic ? (filter as unknown as Record<string, unknown>) : null,
        contact_count: form.is_dynamic ? (form.contact_count ?? 0) : selectedIds.size,
      };
      const saved = isNew ? await createSegment(payload) : await updateSegment(id!, payload);
      if (!saved.is_dynamic) await setSegmentMembers(saved.id, Array.from(selectedIds));
      return saved;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['segments'] });
      queryClient.invalidateQueries({ queryKey: ['segment-members', saved.id] });
      if (saved.is_dynamic) edgeFunctions.resolveSegment(saved.id).catch(() => {});
      navigate(`/segments/${saved.id}`, { replace: true });
    },
  });

  const addCondition = () => setFilter({ ...filter, conditions: [...filter.conditions, { field: 'household_studio', op: 'eq', value: '' }] });
  const updateCondition = (idx: number, cond: FilterCondition) => {
    const prev = filter.conditions[idx];
    let next = cond;
    // Beim Feldwechsel sinnvollen Default-Operator setzen.
    if (prev.field !== cond.field) {
      const isBool = cond.field === 'opt_in' || HOUSEHOLD_BOOLEAN_FIELDS.has(cond.field);
      if (AGE_FIELDS.has(cond.field)) {
        next = { ...cond, op: 'age_between', value: [...DEFAULT_AGE_RANGE] };
      } else if (cond.op === 'age_between') {
        // Weg vom Alters-Feld: age_between gibt es nur dort, also zuruecksetzen.
        next = { ...cond, op: 'eq', value: isBool ? true : '' };
      } else if (cond.field === 'household_member_name' && cond.op === 'eq') {
        next = { ...cond, op: 'contains', value: '' };
      } else if (isBool && !['eq', 'neq', 'is_null', 'is_not_null'].includes(cond.op)) {
        next = { ...cond, op: 'eq', value: true };
      }
    }
    const list = [...filter.conditions]; list[idx] = next;
    setFilter({ ...filter, conditions: list });
  };
  const removeCondition = (idx: number) => setFilter({ ...filter, conditions: filter.conditions.filter((_, i) => i !== idx) });
  const toggleContact = (contactId: string) => {
    const next = new Set(selectedIds);
    if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
    setSelectedIds(next);
  };
  const selectAll = () => setSelectedIds(new Set((allContactsQuery.data ?? []).map((c) => c.id)));
  const selectNone = () => setSelectedIds(new Set());

  // E-Mail-Adressen aus einem freien Text (Komma, Semikolon, Zeilenumbruch, Leerzeichen, Tabs)
  // herausziehen — toleriert Excel/CSV-Spalten, "Name <mail@…>" und Mischformen.
  const extractEmails = (raw: string): string[] => {
    const re = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
    const found = raw.match(re) ?? [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const e of found) {
      const norm = e.toLowerCase();
      if (!seen.has(norm)) { seen.add(norm); out.push(norm); }
    }
    return out;
  };

  const handleBulkImport = () => {
    const list = allContactsQuery.data ?? [];
    const lookup = new Map<string, Contact>();
    for (const c of list) lookup.set(c.email.toLowerCase(), c);
    const emails = extractEmails(bulkText);
    if (emails.length === 0) {
      setBulkResult({ matched: 0, alreadyIn: 0, notFound: [] });
      return;
    }
    const next = new Set(selectedIds);
    let matched = 0;
    let alreadyIn = 0;
    const notFound: string[] = [];
    for (const e of emails) {
      const c = lookup.get(e);
      if (!c) { notFound.push(e); continue; }
      if (next.has(c.id)) alreadyIn++;
      else { next.add(c.id); matched++; }
    }
    setSelectedIds(next);
    setBulkResult({ matched, alreadyIn, notFound });
  };

  const resetBulk = () => { setBulkText(''); setBulkResult(null); };
  const pickerFiltered = useMemo(() => {
    const list = allContactsQuery.data ?? [];
    const q = memberSearch.trim().toLowerCase();
    const matched = q
      ? list.filter((c) =>
          c.email.toLowerCase().includes(q) ||
          (c.first_name?.toLowerCase().includes(q) ?? false) ||
          (c.last_name?.toLowerCase().includes(q) ?? false) ||
          ((c.organization ?? c.school)?.toLowerCase().includes(q) ?? false) ||
          // Familien-Mitglieder: Suche nach "Emilian" findet das Postfach von Raphael,
          // wenn Emilian zum selben Haushalt gehoert.
          c.household_member_names.some((n) => n.toLowerCase().includes(q)) ||
          c.household_organizations.some((o) => o.toLowerCase().includes(q))
        )
      : list;
    // Bereits ausgewaehlte Kontakte zuoberst (stabiler Sort -> Reihenfolge sonst unveraendert).
    return [...matched].sort((a, b) => {
      const aSel = selectedIds.has(a.id) ? 0 : 1;
      const bSel = selectedIds.has(b.id) ? 0 : 1;
      return aSel - bSel;
    });
  }, [allContactsQuery.data, memberSearch, selectedIds]);

  // Dynamische Empfaenger (aus resolve-segment).
  const dynamicRecipients = useMemo<Contact[]>(() => {
    const list = (resolveMutation.data?.contacts ?? []) as Contact[];
    if (!recipientSearch.trim()) return list;
    const q = recipientSearch.toLowerCase();
    return list.filter((c) =>
      c.email.toLowerCase().includes(q) ||
      (c.first_name?.toLowerCase().includes(q) ?? false) ||
      (c.last_name?.toLowerCase().includes(q) ?? false) ||
      ((c.organization ?? c.school)?.toLowerCase().includes(q) ?? false)
    );
  }, [resolveMutation.data, recipientSearch]);

  // Statische Mitglieder (aus selectedIds + Kontakt-Lookup).
  const staticRecipients = useMemo<Contact[]>(() => {
    const list = (allContactsQuery.data ?? []) as Contact[];
    const selected = list.filter((c) => selectedIds.has(c.id));
    if (!recipientSearch.trim()) return selected;
    const q = recipientSearch.toLowerCase();
    return selected.filter((c) =>
      c.email.toLowerCase().includes(q) ||
      (c.first_name?.toLowerCase().includes(q) ?? false) ||
      (c.last_name?.toLowerCase().includes(q) ?? false) ||
      ((c.organization ?? c.school)?.toLowerCase().includes(q) ?? false) ||
      c.household_member_names.some((n) => n.toLowerCase().includes(q))
    );
  }, [allContactsQuery.data, selectedIds, recipientSearch]);

  if (!isNew && segmentQuery.isLoading) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <button className="btn btn-ghost" onClick={() => navigate('/segments')}><ChevronLeft size={16} /> Zurück</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="loading-spinner" /></div>
      </div>
    );
  }

  const titleName = form.name || segmentQuery.data?.name || (isNew ? 'Neue Gruppe' : 'Gruppe bearbeiten');

  // Welche Empfaenger-Card oben anzeigen?
  const showDynamicCard = !isNew && form.is_dynamic === true;
  const showStaticCard  = !isNew && form.is_dynamic === false;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/segments')}><ChevronLeft size={16} /> Zurück</button>
      </div>

      <PageHeader
        title={titleName}
        subtitle="Empfänger-Gruppe für Kampagnen definieren"
        actions={
          <button className="btn btn-primary" onClick={() => saveMutation.mutate()} disabled={!form.name || saveMutation.isPending}>
            <Save size={16} /> {saveMutation.isPending ? 'Speichere...' : 'Speichern'}
          </button>
        }
      />

      {/* Mitglieder-Card oben — fuer DYNAMISCHE Gruppen. */}
      {showDynamicCard && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title"><Users size={18} style={{ verticalAlign: '-3px' }} /> Mitglieder dieser Gruppe</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => resolveMutation.mutate()} disabled={resolveMutation.isPending}>
                {resolveMutation.isPending ? 'Aktualisiere...' : 'Neu prüfen'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowRecipients((v) => !v)}>
                {showRecipients ? <><ChevronUp size={14} /> Einklappen</> : <><ChevronDown size={14} /> Anzeigen</>}
              </button>
            </div>
          </div>

          {resolveMutation.isPending && !resolveMutation.data && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>
          )}
          {resolveMutation.isError && (
            <div className="alert alert-danger"><Info size={16} /><div>Fehler: {(resolveMutation.error as Error).message}</div></div>
          )}
          {resolveMutation.data && (
            <>
              <div className="alert" style={{ background: 'var(--success-light)', color: 'var(--success-dark)', marginBottom: '1rem' }}>
                <Users size={16} />
                <div><strong>{formatNumber(resolveMutation.data.contact_count)}</strong> Empfänger erfüllen die Filterkriterien.</div>
              </div>
              {showRecipients && resolveMutation.data.contacts.length > 0 && (
                <>
                  <RecipientTable contacts={dynamicRecipients} searchValue={recipientSearch} onSearchChange={setRecipientSearch} emptyMessage="Keine Treffer." />
                  {resolveMutation.data.contact_count > resolveMutation.data.contacts.length && (
                    <p className="form-hint" style={{ marginTop: '0.5rem' }}>
                      ⚠ Anzeige auf die ersten {resolveMutation.data.contacts.length} von {resolveMutation.data.contact_count} Empfängern begrenzt.
                    </p>
                  )}
                </>
              )}
              {showRecipients && resolveMutation.data.contacts.length === 0 && (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <Users size={32} /><p style={{ marginTop: '0.5rem' }}>Diese Gruppe hat aktuell 0 Empfänger.</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Mitglieder-Card oben — fuer STATISCHE Gruppen. */}
      {showStaticCard && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title"><Users size={18} style={{ verticalAlign: '-3px' }} /> Mitglieder dieser Gruppe</h2>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowRecipients((v) => !v)}>
              {showRecipients ? <><ChevronUp size={14} /> Einklappen</> : <><ChevronDown size={14} /> Anzeigen</>}
            </button>
          </div>
          <div className="alert" style={{ background: 'var(--success-light)', color: 'var(--success-dark)', marginBottom: '1rem' }}>
            <Users size={16} />
            <div><strong>{formatNumber(selectedIds.size)}</strong> Kontakte handverlesen ausgewählt. Bearbeiten unten.</div>
          </div>
          {allContactsQuery.isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>
          )}
          {showRecipients && allContactsQuery.data && selectedIds.size > 0 && (
            <RecipientTable contacts={staticRecipients} searchValue={recipientSearch} onSearchChange={setRecipientSearch} emptyMessage="Keine Treffer im Suchfilter." />
          )}
          {showRecipients && allContactsQuery.data && selectedIds.size === 0 && (
            <div className="empty-state" style={{ padding: '2rem' }}>
              <Users size={32} /><p style={{ marginTop: '0.5rem' }}>Noch keine Kontakte ausgewählt. Wähle sie unten aus.</p>
            </div>
          )}
        </div>
      )}

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <Info size={16} />
        <div>
          <strong>Was ist eine Gruppe?</strong> Eine Gruppe legt fest, <em>wer</em> eine Kampagne bekommt.
          {' '}<strong>Dynamisch</strong> = Filter, aktualisiert sich automatisch.
          {' '}<strong>Statisch</strong> = handverlesene Kontakte.
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input className="form-input" placeholder="z.B. Aktive Mitglieder Berlin"
            value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Beschreibung</label>
          <textarea className="form-textarea" rows={2} placeholder="Wofür wird diese Gruppe genutzt?"
            value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Typ</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <input type="checkbox" checked={form.is_dynamic ?? true}
              onChange={(e) => setForm({ ...form, is_dynamic: e.target.checked })}
              style={{ accentColor: 'var(--primary)', width: 18, height: 18 }} />
            <span><strong>Dynamische Gruppe</strong> — Filter-basiert, aktualisiert sich automatisch</span>
          </label>
          <p className="form-hint">Nicht angehakt = <strong>Statische Gruppe</strong>.</p>
        </div>

        {/* Sichtbarkeits-Scope + Ablauf */}
        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-light)' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Gültig bis (optional)</label>
            <input type="date" className="form-input"
              value={form.valid_until ?? ''}
              onChange={(e) => setForm({ ...form, valid_until: e.target.value || null })} />
            <p className="form-hint">
              Nach diesem Datum kann die Gruppe nicht mehr für Kampagnen genutzt werden. Leer lassen = unbegrenzt.
            </p>
            {form.valid_until && !isSegmentUsable({ ...form, valid_until: form.valid_until } as Segment) && (
              <p className="form-hint" style={{ color: 'var(--danger-dark)' }}>
                ⚠ Diese Gruppe ist <strong>abgelaufen</strong> und wird im Kampagnen-Editor blockiert.
              </p>
            )}
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Sichtbar für Studio-Leiter (optional)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', maxHeight: 120, overflowY: 'auto', padding: '0.4rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--bg-primary)' }}>
              {STUDIO_OPTIONS.map((s) => {
                const checked = (form.studio_scope ?? []).includes(s);
                return (
                  <label key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.825rem', padding: '0.2rem 0.4rem', border: '1px solid var(--border-light)', borderRadius: '0.4rem', background: checked ? 'var(--primary-light)' : 'transparent', color: checked ? 'var(--primary)' : 'var(--text-primary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked}
                      onChange={(e) => {
                        const cur = form.studio_scope ?? [];
                        setForm({ ...form, studio_scope: e.target.checked ? [...cur, s] : cur.filter(x => x !== s) });
                      }}
                      style={{ accentColor: 'var(--primary)' }} />
                    {s}
                  </label>
                );
              })}
            </div>
            <p className="form-hint">
              Leer = Gruppe ist <strong>global</strong> (Admin + Redakteur). Studio anhaken = Studio-Leiter dieses Studios sehen die Gruppe ebenfalls.
            </p>
          </div>
        </div>

        {isAdmin && (
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
              <input type="checkbox" checked={form.admin_only ?? false}
                onChange={(e) => setForm({ ...form, admin_only: e.target.checked })}
                style={{ accentColor: 'var(--primary)', width: 18, height: 18 }} />
              <span><strong>Nur für Admins sichtbar</strong> — überschreibt Studio-Scope. Typisch für [Audit]-Gruppen oder interne Versand-Listen.</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginTop: '0.75rem' }}>
              <input type="checkbox" checked={form.always_included ?? false}
                onChange={(e) => setForm({ ...form, always_included: e.target.checked })}
                style={{ accentColor: 'var(--primary)', width: 18, height: 18 }} />
              <span><strong>Bei jedem Versand dabei</strong> — die Gruppe bekommt jede Mail automatisch in Kopie, auch in jedem Brevo-Entwurf aus dem Mailtool; sie wird dafür immer nach Brevo gespiegelt. Nur für interne Verteiler (Studios, Studioleitungen, Redaktion, Geschäftsführung).</span>
            </label>
          </div>
        )}
      </div>

      {/* Brevo-Sync */}
      {!isNew && segmentQuery.data && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h2 className="card-title">
              {segmentQuery.data.brevo_sync_enabled ? <Cloud size={18} style={{ verticalAlign: '-3px' }} /> : <CloudOff size={18} style={{ verticalAlign: '-3px' }} />}
              {' '}Brevo-Sync
            </h2>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
            <input type="checkbox"
              checked={segmentQuery.data.brevo_sync_enabled}
              disabled={brevoToggleMutation.isPending}
              onChange={(e) => brevoToggleMutation.mutate(e.target.checked)}
              style={{ accentColor: 'var(--primary)', width: 18, height: 18 }} />
            <span><strong>In Brevo spiegeln</strong> — stuendliche automatische Aktualisierung der gleichnamigen Brevo-Liste</span>
          </label>

          {segmentQuery.data.brevo_sync_enabled && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '0.25rem 1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                <span>Brevo-Liste</span>
                <span>
                  {segmentQuery.data.brevo_list_id
                    ? <>#{segmentQuery.data.brevo_list_id} <span style={{ color: 'var(--text-tertiary)' }}>(in Brevo unter „randori-mail-segment-…" sichtbar)</span></>
                    : <em>noch nicht angelegt</em>}
                </span>
                <span>Zuletzt synchronisiert</span>
                <span>
                  {segmentQuery.data.brevo_last_synced_at
                    ? formatDateTime(segmentQuery.data.brevo_last_synced_at)
                    : <em>noch nie</em>}
                </span>
              </div>

              {segmentQuery.data.brevo_sync_error && (
                <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                  <AlertTriangle size={16} />
                  <div>Letzter Sync schlug fehl: <code>{segmentQuery.data.brevo_sync_error}</code></div>
                </div>
              )}

              {brevoSyncMutation.isError && (
                <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                  <AlertTriangle size={16} />
                  <div>Fehler: {(brevoSyncMutation.error as Error).message}</div>
                </div>
              )}
              {brevoSyncMutation.isSuccess && brevoSyncMutation.data && (
                <div className="alert" style={{ background: 'var(--success-light)', color: 'var(--success-dark)', marginBottom: '1rem' }}>
                  <Info size={16} />
                  <div>
                    Sync erfolgreich: {brevoSyncMutation.data.total} Empfaenger in Brevo-Liste #{brevoSyncMutation.data.list_id}
                    {' '}(+{brevoSyncMutation.data.added} neu, −{brevoSyncMutation.data.removed} entfernt).
                  </div>
                </div>
              )}

              <button className="btn btn-secondary"
                onClick={() => brevoSyncMutation.mutate()}
                disabled={brevoSyncMutation.isPending}>
                <RefreshCw size={14} style={{ marginRight: 6 }} />
                {brevoSyncMutation.isPending ? 'Synchronisiere…' : 'Jetzt synchronisieren'}
              </button>
            </>
          )}
        </div>
      )}

      {form.is_dynamic && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Filter</h2>
            <select className="form-select" style={{ maxWidth: '220px' }}
              value={filter.operator}
              onChange={(e) => setFilter({ ...filter, operator: e.target.value as 'AND' | 'OR' })}>
              <option value="AND">Alle Bedingungen müssen zutreffen</option>
              <option value="OR">Mindestens eine Bedingung trifft zu</option>
            </select>
          </div>

          {filter.conditions.map((cond, idx) => {
            const isAge     = AGE_FIELDS.has(cond.field);
            const isBoolean = cond.field === 'opt_in' || HOUSEHOLD_BOOLEAN_FIELDS.has(cond.field);
            const opLabels  = isAge ? OP_LABELS_AGE
                            : isBoolean ? { ...OP_LABELS_DEFAULT, ...OP_LABELS_BOOLEAN }
                            : OP_LABELS_DEFAULT;
            const showValue = cond.op !== 'is_null' && cond.op !== 'is_not_null';
            return (
              <div key={idx} className="filter-row" style={{ display: 'grid', gridTemplateColumns: '210px 200px 1fr auto', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                <select className="form-select" value={cond.field}
                  onChange={(e) => updateCondition(idx, { ...cond, field: e.target.value as FilterCondition['field'] })}>
                  {Object.entries(FIELD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select className="form-select" value={cond.op}
                  onChange={(e) => updateCondition(idx, { ...cond, op: e.target.value as FilterCondition['op'] })}>
                  {Object.entries(opLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {showValue ? (
                  <>
                    {(cond.field === 'organization' || cond.field === 'household_studio') && (
                      <select className="form-select" value={typeof cond.value === 'string' ? cond.value : ''}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value })}>
                        <option value="">— Studio wählen —</option>
                        {ORGANIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    {(cond.field === 'household_is_member' || cond.field === 'household_has_trainer') && (
                      <select className="form-select" value={cond.value === true ? 'true' : 'false'}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value === 'true' })}>
                        <option value="true">Ja</option>
                        <option value="false">Nein</option>
                      </select>
                    )}
                    {cond.field === 'household_member_name' && (
                      <input className="form-input" placeholder="z.B. Emilian"
                        value={typeof cond.value === 'string' ? cond.value : ''}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value })} />
                    )}
                    {cond.field === 'school' && (
                      <select className="form-select" value={typeof cond.value === 'string' ? cond.value : ''}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value })}>
                        <option value="">— Schule wählen —</option>
                        {SCHOOLS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    {cond.field === 'member_status' && (
                      <select className="form-select" value={typeof cond.value === 'string' ? cond.value : ''}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value })}>
                        <option value="">— Status wählen —</option>
                        {MEMBER_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    {cond.field === 'opt_in' && (
                      <select className="form-select" value={cond.value === true ? 'true' : 'false'}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value === 'true' })}>
                        <option value="true">Ja</option>
                        <option value="false">Nein</option>
                      </select>
                    )}
                    {cond.field === 'tags' && (
                      <input className="form-input" placeholder="z.B. trainer"
                        value={typeof cond.value === 'string' ? cond.value : ''}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value })} />
                    )}
                    {cond.field === 'bounce_status' && (
                      <select className="form-select" value={typeof cond.value === 'string' ? cond.value : ''}
                        onChange={(e) => updateCondition(idx, { ...cond, value: e.target.value })}>
                        <option value="">— Bounce-Typ —</option>
                        <option value="soft">Soft</option>
                        <option value="hard">Hard</option>
                      </select>
                    )}
                    {isAge && (() => {
                      const range = Array.isArray(cond.value) ? (cond.value as number[]) : DEFAULT_AGE_RANGE;
                      const setRange = (lo: number, hi: number) =>
                        updateCondition(idx, { ...cond, op: 'age_between', value: [lo, hi] });
                      return (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input type="number" min={0} max={120} className="form-input" placeholder="Min"
                            style={{ maxWidth: 90 }}
                            value={range[0] ?? ''}
                            onChange={(e) => setRange(e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10)), range[1] ?? 120)} />
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>bis</span>
                          <input type="number" min={0} max={120} className="form-input" placeholder="Max"
                            style={{ maxWidth: 90 }}
                            value={range[1] ?? ''}
                            onChange={(e) => setRange(range[0] ?? 0, e.target.value === '' ? 120 : Math.max(0, parseInt(e.target.value, 10)))} />
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>Jahre</span>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <span style={{ fontStyle: 'italic', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>(kein Wert nötig)</span>
                )}
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                  onClick={() => removeCondition(idx)} disabled={filter.conditions.length === 1}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}

          <button className="btn btn-ghost btn-sm" onClick={addCondition}><Plus size={14} /> Bedingung hinzufügen</button>
        </div>
      )}

      {!form.is_dynamic && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Kontakte auswählen</h2>
            <span className="status-badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>
              <UserCheck size={11} /> {formatNumber(selectedIds.size)} ausgewählt
            </span>
          </div>
          {/* Bulk-Import-Toolbar: auch beim Anlegen einer neuen Gruppe verfuegbar.
              Beim Speichern werden die ausgewaehlten Kontakte mit dem neuen Segment persistiert. */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {!isNew && (
              <div className="search-input-wrapper" style={{ flex: 1, minWidth: 260 }}>
                <Search size={16} />
                <input className="search-input" placeholder="Kontakte suchen..."
                  value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />
              </div>
            )}
            <button className="btn btn-secondary btn-sm"
              onClick={() => { setBulkOpen((v) => !v); if (bulkOpen) resetBulk(); }}>
              <ClipboardPaste size={14} /> {bulkOpen ? 'Liste schließen' : 'Aus Liste importieren'}
            </button>
            {!isNew && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={selectAll}>Alle auswählen ({formatNumber(pickerFiltered.length)})</button>
                <button className="btn btn-ghost btn-sm" onClick={selectNone}>Auswahl entfernen</button>
              </>
            )}
            {isNew && selectedIds.size > 0 && (
              <span className="status-badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>
                <UserCheck size={11} /> {formatNumber(selectedIds.size)} vorausgewählt
              </span>
            )}
          </div>

          {isNew && selectedIds.size === 0 && !bulkOpen && (
            <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
              <Users size={16} /><div>Tipp: Du kannst hier direkt eine Liste von E-Mail-Adressen einfügen und sie als Kontakte zuordnen — oder erst die Gruppe speichern, um die volle Kontaktliste zu sehen.</div>
            </div>
          )}
          {isNew && selectedIds.size > 0 && (
            <div className="alert" style={{ background: 'var(--success-light)', color: 'var(--success-dark)', marginBottom: '1rem' }}>
              <UserCheck size={16} />
              <div>
                <strong>{formatNumber(selectedIds.size)}</strong> Kontakte vorausgewählt. Klicke oben auf <strong>Speichern</strong>, um die Gruppe inklusive Mitglieder anzulegen.
              </div>
            </div>
          )}

          {bulkOpen && (
            <div className="card" style={{ marginBottom: '1rem', background: 'var(--surface-secondary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <strong style={{ fontSize: '0.95rem' }}>E-Mail-Liste einfügen</strong>
                <button className="btn btn-ghost btn-sm" onClick={() => { setBulkOpen(false); resetBulk(); }}>
                  <X size={14} />
                </button>
              </div>
              <p className="form-hint" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                Adressen frei reinkopieren — egal ob aus Excel, Outlook oder Notizen.
                Komma, Semikolon, Zeilenumbruch oder Leerzeichen sind als Trenner ok.
                Wir suchen die passenden Kontakte heraus und übernehmen sie in die Auswahl.
                <strong> Speichern</strong> übernimmt die Liste dauerhaft in die Gruppe.
              </p>
              <textarea
                className="form-textarea"
                style={{ minHeight: 140, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.85rem' }}
                placeholder={'max.mustermann@randori-pro.de\nlea@example.com; tobi@example.com\n"Anna Beispiel" <anna@example.com>'}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn-primary btn-sm"
                  onClick={handleBulkImport}
                  disabled={!bulkText.trim() || allContactsQuery.isLoading}>
                  <Check size={14} /> Adressen zuordnen
                </button>
                <button className="btn btn-ghost btn-sm" onClick={resetBulk} disabled={!bulkText && !bulkResult}>
                  Zurücksetzen
                </button>
                {allContactsQuery.isLoading && (
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>Kontakt-Liste wird geladen …</span>
                )}
              </div>

              {bulkResult && (
                <div style={{ marginTop: '0.75rem' }}>
                  {bulkResult.matched > 0 && (
                    <div className="alert" style={{ background: 'var(--success-light)', color: 'var(--success-dark)', marginBottom: '0.5rem' }}>
                      <Check size={16} />
                      <div>
                        <strong>{formatNumber(bulkResult.matched)}</strong> {bulkResult.matched === 1 ? 'Kontakt wurde' : 'Kontakte wurden'} zur Auswahl hinzugefügt.
                        {bulkResult.alreadyIn > 0 && <> {formatNumber(bulkResult.alreadyIn)} waren bereits in der Gruppe.</>}
                        {' '}Mit <strong>Speichern</strong> werden sie übernommen.
                      </div>
                    </div>
                  )}
                  {bulkResult.matched === 0 && bulkResult.alreadyIn === 0 && bulkResult.notFound.length === 0 && (
                    <div className="alert alert-info">
                      <Info size={16} />
                      <div>Im Text wurden keine E-Mail-Adressen erkannt.</div>
                    </div>
                  )}
                  {bulkResult.matched === 0 && bulkResult.alreadyIn > 0 && (
                    <div className="alert alert-info">
                      <Info size={16} />
                      <div>Alle {formatNumber(bulkResult.alreadyIn)} erkannten Adressen waren bereits in der Auswahl.</div>
                    </div>
                  )}
                  {bulkResult.notFound.length > 0 && (
                    <div className="alert alert-warning">
                      <AlertTriangle size={16} />
                      <div>
                        <strong>{formatNumber(bulkResult.notFound.length)}</strong> {bulkResult.notFound.length === 1 ? 'Adresse wurde' : 'Adressen wurden'} nicht in den vorhandenen Kontakten gefunden:
                        <div style={{ marginTop: '0.4rem', maxHeight: 120, overflowY: 'auto', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.8rem', background: 'var(--bg-primary)', padding: '0.5rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-light)' }}>
                          {bulkResult.notFound.map((e) => <div key={e}>{e}</div>)}
                        </div>
                        <p className="form-hint" style={{ margin: '0.4rem 0 0' }}>
                          Diese Adressen existieren noch nicht als Kontakt. Lege sie erst unter <a href="/contacts" style={{ color: 'var(--primary)' }}>Kontakte</a> an, dann nochmal importieren.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!isNew && (
            <>
              {allContactsQuery.isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>
              ) : pickerFiltered.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} /><h3>Keine Kontakte gefunden</h3>
                  <p>{memberSearch ? 'Keine Treffer.' : <>Lege Kontakte unter <a href="/contacts" style={{ color: 'var(--primary)' }}>Kontakte</a> an.</>}</p>
                </div>
              ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
                  <table className="data-table" style={{ border: 'none', borderRadius: 0 }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr><th style={{ width: '40px' }}></th><th>E-Mail</th><th>Name</th><th>Studio</th><th>Opt-In</th></tr>
                    </thead>
                    <tbody>
                      {pickerFiltered.map((c) => {
                        const isSelected = selectedIds.has(c.id);
                        const ineligible = !c.opt_in || c.unsubscribed_at || c.bounce_status === 'hard' || c.is_active === false;
                        return (
                          <tr key={c.id} className="clickable" onClick={() => toggleContact(c.id)}
                            style={{ background: isSelected ? 'var(--primary-light)' : undefined, opacity: ineligible ? 0.55 : 1 }}>
                            <td>
                              <input type="checkbox" checked={isSelected}
                                onChange={() => toggleContact(c.id)} onClick={(e) => e.stopPropagation()}
                                style={{ accentColor: 'var(--primary)', width: 18, height: 18 }} />
                            </td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{c.email}</td>
                            <td>
                              {[c.first_name, c.last_name].filter(Boolean).join(' ') || <em style={{ color: 'var(--text-tertiary)' }}>—</em>}
                              {c.household_member_count > 1 && (
                                <HouseholdBadge
                                  email={c.email}
                                  memberCount={c.household_member_count}
                                  memberNames={c.household_member_names}
                                  primaryCrmId={c.crm_id} />
                              )}
                            </td>
                            <td>
                              {c.household_organizations.length > 1
                                ? c.household_organizations.join(', ')
                                : (c.organization ?? c.school ?? <em style={{ color: 'var(--text-tertiary)' }}>—</em>)}
                            </td>
                            <td>
                              {c.unsubscribed_at ? (
                                <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)' }}>abgemeldet</span>
                              ) : c.is_active === false ? (
                                <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>inaktiv</span>
                              ) : c.opt_in ? (
                                <span className="status-badge" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}>versendbar</span>
                              ) : (
                                <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>kein Opt-In</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="form-hint" style={{ marginTop: '1rem' }}>
                <strong>Hinweis:</strong> Beim Versand werden nur Kontakte mit aktivem Opt-In berücksichtigt.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
